/**
 * Custom error classes for API interactions
 */

export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public response?: unknown,
    /** Correlates this failure with the matching CloudWatch log lines — see the
     * `X-Request-Id` response header set by `apps/api/src/lib/http/request-id-middleware.ts`. */
    public requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class NetworkError extends Error {
  constructor(message: string = 'Network request failed') {
    super(message);
    this.name = 'NetworkError';
  }
}

export class ValidationError extends Error {
  constructor(
    message: string,
    public fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ValidationError';
  }
}

export class AuthError extends Error {
  constructor(message: string = 'Authentication required') {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * Check if error is an API error
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

/**
 * Check if error is an auth error (401/403)
 */
export function isAuthError(error: unknown): error is AuthError {
  return (
    error instanceof AuthError ||
    (error instanceof ApiError && (error.statusCode === 401 || error.statusCode === 403))
  );
}

/**
 * Check if error is a validation error
 */
export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

/**
 * Extract user-friendly error message from any error type
 */
export function extractErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof ApiError) {
    // Try to extract message from response
    if (error.response && typeof error.response === 'object' && 'message' in error.response) {
      return String(error.response.message);
    }
    return error.message || `API Error (${error.statusCode})`;
  }

  if (error instanceof NetworkError) {
    return 'Network error. Please check your connection and try again.';
  }

  if (error instanceof AuthError) {
    return 'Authentication required. Please sign in.';
  }

  if (error instanceof ValidationError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
}

/**
 * Get HTTP status code from error
 */
export function getErrorStatusCode(error: unknown): number | null {
  if (error instanceof ApiError) {
    return error.statusCode;
  }
  return null;
}
